import React from "react";
import { useTranslation } from "react-i18next";
import type { LanguageCode } from "src/i18n/config";
import { getAvailableLanguages } from "src/services/configService";

import { SelectField } from "@shared/form/SelectField/SelectField";
import { SingleSelectFieldProps } from "@shared/form/SelectField/SelectField.types";

type LanguageSwitcherProps = {
    showLabel?: boolean;
    fullWidth?: boolean;
};

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ showLabel = true, fullWidth = false }) => {
    const { t, i18n } = useTranslation(["common", "pages"]);

    // Extract base language code (browser returns "de-DE", we need "de")
    const currentLanguage = i18n.language?.split("-")[0] || "en";

    const languageLabels: Record<LanguageCode, string> = {
        de: t("common:language.de"),
        en: t("common:language.en"),
        fr: t("common:language.fr"),
        pl: t("common:language.pl"),
        ja: t("common:language.ja"),
        ru: t("common:language.ru"),
    };

    const options = getAvailableLanguages().map((code) => ({
        label: languageLabels[code],
        value: code,
    }));

    const onChange: SingleSelectFieldProps["onChange"] = (_event, selectedOption) => {
        if (typeof selectedOption?.value !== "string") {
            return;
        }

        void i18n.changeLanguage(selectedOption?.value).then(() => {
            // Force reload page to ensure translations load properly from i18next config file.
            // This is necessary because we use translations outside of components (in functions declared
            // outside components, and when declaring columns for TanStack Table library) without the useTranslation hook.
            window.location.reload();
        });
    };

    return (
        <SelectField
            disableSorting
            fullWidth={fullWidth}
            value={options.find((option) => option.value === currentLanguage)}
            options={options}
            onChange={onChange}
            {...(showLabel ? { label: t("pages:settingsPage.language.label") } : {})}
        />
    );
};

export default LanguageSwitcher;
